import { useToast } from '@chakra-ui/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useTranslation } from 'next-i18next';

import type { Cart, ConfirmCartResult } from '@getjust/gateway';

import { useShop } from '$hooks/queries';
import { useGlobalPropertiesTrackAtom, usePaymentStatusAtom, useROFormMessageAtom } from '$hooks/state';
import { CART_QUERY_KEY, USE_AUTH_LEGACY } from '$src/constants';
import { useExtensionContext } from '$src/context/ExtensionContext';
import { useAuth } from '$src/hooks/auth';
import { useBaseApiUrl } from '$src/hooks/client/useBaseApiUrl';
import { AnalyticsEventName, useTrackAnalytics } from '$src/hooks/client/useTrackAnalytics';
import { useActionExecutor, useExtensionManager } from '$src/hooks/extensions';
import { useExtensions } from '$src/hooks/extensions/useExtensions';
import { useAddAddress } from '$src/hooks/mutations/useAddAddress';
import { useCorrectionsAtom } from '$src/hooks/state/useCorrectionsAtom';
import { useErrorAtom } from '$src/hooks/state/useErrorAtom';
import { gateway } from '$src/http';
import { ConfirmInput } from '$src/pages/api/[shopId]/[cartId]/confirm';

interface BillingAddressToSave {
  first_name: string;
  last_name: string;
  address1: string;
  address2: string;
  city: string;
  country: string;
  country_code: string;
  province: string;
  zip: string;
  phone: string;
}

export interface CreateOrderInput extends ConfirmInput {
  saveBillingAddress?: BillingAddressToSave;
}

export const CREATE_ORDER_MUTATION_KEY = 'MUTATION/CREATE_ORDER';

export const useCreateOrder = () => {
  const { initCorrections, clearCorrections } = useCorrectionsAtom();
  const { onPaymentStatusChange } = usePaymentStatusAtom();
  const { data: shop } = useShop();
  const message = useROFormMessageAtom();
  const { t, i18n } = useTranslation('common');
  const baseUrl = useBaseApiUrl();
  const { setIsAuthenticated, setLegacyState } = useAuth();
  const queryClient = useQueryClient();
  const { onErrorChange } = useErrorAtom();
  const { track, getPosthogSessionId } = useTrackAnalytics();
  const { globalPropertiesTrack: trackingProperties } = useGlobalPropertiesTrackAtom();
  const { mutateAsync: addAddress } = useAddAddress();

  const toast = useToast();

  const { fields, attributes } = useExtensionContext();
  const extensions = useExtensions();
  const { executeActions: getExtensionActions } = useExtensionManager(extensions);
  const { executeActions: performActions } = useActionExecutor();

  return useMutation({
    mutationFn: async (input: CreateOrderInput) => {
      const { saveBillingAddress: _saveBillingAddress, ...confirmInput } = input;
      console.warn('[useCreateOrder] CONTEXT-BASED execution with on-order-creation actions');

      // Step 1: Execute on-order-creation actions - with context field values!
      const actionsToExecute = getExtensionActions('on-order-creation', {
        cartId: confirmInput.cart.id,
        shopId: shop?.id || '',
        email: confirmInput.customer.email,
        fieldValues: fields,
      });

      // Step 2: Execute actions and get custom attributes
      const { customAttributes, addedProducts } = await performActions(actionsToExecute);

      console.warn('[useCreateOrder] Execution results:', {
        customAttributes,
        addedProducts,
      });

      // Step 3: Apply ALL custom attributes (from actions + local storage) to order input
      const existingCustomAttributes = confirmInput.cart.customAttributes || [];
      const allCustomAttributes = [...customAttributes, ...attributes]; // From on-order-creation + discount codes

      console.warn('[useCreateOrder] Merging custom attributes:', {
        fromOrderCreation: customAttributes,
        fromDiscountCodes: attributes,
        total: allCustomAttributes,
      });

      const updatedCustomAttributes = [...existingCustomAttributes];

      // Upsert each custom attribute (from both sources)
      for (const newAttr of allCustomAttributes) {
        const existingIndex = updatedCustomAttributes.findIndex((attr) => attr.key === newAttr.key);

        if (existingIndex >= 0) {
          // Update existing attribute
          updatedCustomAttributes[existingIndex] = newAttr;
          console.warn(
            `[useCreateOrder] Updated existing custom attribute: ${newAttr.key} = ${newAttr.value}`,
          );
        } else {
          // Add new attribute
          updatedCustomAttributes.push(newAttr);
          console.warn(`[useCreateOrder] Added new custom attribute: ${newAttr.key} = ${newAttr.value}`);
        }
      }

      const modifiedInput = {
        ...confirmInput,
        cart: {
          ...confirmInput.cart,
          customAttributes: updatedCustomAttributes,
        },
        posthogSessionId: getPosthogSessionId(),
        trackingProperties,
        analytics: message?.analytics,
      };

      console.warn('[useCreateOrder] Final order input:', modifiedInput);

      return gateway.post<ConfirmCartResult>(`${baseUrl}/confirm`, modifiedInput);
    },
    mutationKey: [CREATE_ORDER_MUTATION_KEY, baseUrl],
    onError: (error: AxiosError<ConfirmCartResult>, variables) => {
      switch (error?.response?.data?.outcome) {
        case 'notConfirmed':
          if (error.response.data?.reason === 'merchantCorrections') {
            const updatedCart = error.response?.data.cart;
            if (updatedCart) {
              queryClient.setQueryData<Cart>([CART_QUERY_KEY, error.response.data.cart.id], updatedCart);
              if (!updatedCart.corrections?.length) {
                clearCorrections();
              } else {
                initCorrections(updatedCart.corrections);
              }
            }
          }
          if (error.response.data?.reason === 'priceMismatch') {
            track(AnalyticsEventName.PRICE_MISS_MATCH, {
              variantsIds: JSON.stringify(message?.cart?.lineItems?.map((item) => item.variantId) ?? []),
            });
            toast({
              description: t('errors.commons.priceMismatch'),
              status: 'error',
              position: 'top',
              duration: 10000,
              isClosable: true,
            });

            const data = error.response.data as { url?: string } & ConfirmCartResult;
            if (data.url) {
              const url = data.url;
              setTimeout(() => {
                location.href = url;
              }, 5000);
            }
          }
          if (error.response.data.reason === 'threedSecured') {
            onPaymentStatusChange({
              display: true,
              url: error.response.data.nextAction ?? '',
              cartId: error.response.data.cartId,
              opened: true,
            });
          }
          if (['cannotAddSource', 'paymentError'].indexOf(error.response.data.reason) !== -1) {
            const errorCode =
              (error.response.data.reason === 'paymentError'
                ? error.response.data?.code
                : // @ts-expect-error - error
                  error.response.data.error?.error?.code) ?? 'card_declined';
            const titleError = `errors.stripe.${errorCode}.title`;
            const sentenceError = `errors.stripe.${errorCode}.sentence`;
            const genericTitleError = `errors.stripe.generic_decline.title`;
            const genericSentenceError = `errors.stripe.generic_decline.sentence`;
            onErrorChange({
              title: i18n.exists(titleError) ? t(titleError) : t(genericTitleError),
              sentence: i18n.exists(sentenceError) ? t(sentenceError) : t(genericSentenceError),
            });
            if (['expired_card', 'incorrect_cvc'].includes(errorCode)) {
              track(AnalyticsEventName.ORDER_CREATION_EXECUTED, {
                output: 'Wrong date/crypto',
                paymentMethod: variables.paymentMethod,
              });
            } else {
              track(AnalyticsEventName.ORDER_CREATION_EXECUTED, {
                output: errorCode,
                paymentMethod: variables.paymentMethod,
              });
            }
          }
          break;
        default:
          track(AnalyticsEventName.CREATE_ORDER_FAILED, { seller: shop?.name });
          break;
      }
    },
    onSettled: async (response, error, variables) => {
      if (error?.response?.data?.cart?.id) {
        queryClient.setQueryData<Cart>(
          [CART_QUERY_KEY, error.response.data.cart.id],
          error?.response?.data?.cart,
        );
      }

      if (response?.data?.cart?.id) {
        queryClient.setQueryData<Cart>([CART_QUERY_KEY, response.data.cart.id], response?.data?.cart);
      }

      const hasLogin =
        // @ts-expect-error - expected
        response?.data?.login ||
        // @ts-expect-error - expected
        error?.response?.data?.login;

      if (hasLogin) {
        if (USE_AUTH_LEGACY) {
          setLegacyState({
            accessToken:
              // @ts-expect-error always defined
              response?.data?.login?.accessToken ?? error?.response?.data?.login?.accessToken ?? '',
            refreshToken:
              // @ts-expect-error always defined
              response?.data?.login?.refreshToken ?? error?.response?.data?.login?.refreshToken ?? '',
          });
        }
        setIsAuthenticated(true);
      }

      if (variables.saveBillingAddress) {
        try {
          await addAddress({
            address: variables.saveBillingAddress,
            is_default_billing: true,
            is_default_shipping: false,
          });
        } catch (e) {
          console.warn('[useCreateOrder] Error adding billing address');
        }
      }

      switch (response?.data?.outcome) {
        case 'confirmed':
          onPaymentStatusChange({
            display: false,
            url: undefined,
            cartId: response.data.cartId,
            opened: true,
          });
          break;
      }
    },
  });
};
